import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    IPatient,
    sortPatients,
    selectPatientSort,
    selectCurrentPatient,
    setFilteredPatientsCount,
} from "./patientsSlice";
import { useGetPatientsQuery, useGetTagsQuery } from "./dataAccess";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { CircularProgress, TableContainer, TablePagination } from "@mui/material";
import { Box } from "@mui/system";
import DisplayText from "../../components/DisplayText/DisplayText";
import { getSelectedTrial } from "../auth/login/loginSlice";
import PatientTable from "./PatientTable";
import PatientSearch from "./PatientSearch";
import useQueryParams from "../../hooks/useQueryParams/useQueryParams";
import useFilters, { FilterGroup, FilterOption, FilterType } from "./useFilters";
import usePermissions from "../permissions/usePermissions";
import useRoles from "../permissions/useRoles";
import { useGetWorkflowStatesQuery } from "../workflow/dataAccess";
import { useUpdateCurrentGroupMutation } from "../auth/dataAccess";
import { WorkflowState } from "../workflow/nebula.service";

const PatientsDashboard = () => {
    const dispatch = useAppDispatch();
    const { hasPermissions } = usePermissions();
    const { userHasRole } = useRoles();
    const canManageRemovedSubjects = userHasRole("Client Admin") || userHasRole("Master Admin");
    const selectedTrial = useAppSelector(getSelectedTrial);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(50);
    const [stablePatients, setStablePatients] = useState<IPatient[]>([]);
    const [stableTotal, setStableTotal] = useState<number>(0);
    const [requestTrigger, setRequestTrigger] = useState<"sort" | "page" | "search" | "filter" | null>(null);

    const { data: workflowSates } = useGetWorkflowStatesQuery(selectedTrial?.uuid || "", {
        skip: !selectedTrial || !selectedTrial.workflowActive,
        refetchOnMountOrArgChange: true,
    });

    const { data: tagOptions } = useGetTagsQuery(selectedTrial?.uuid || "", {
        skip: !selectedTrial || !selectedTrial.showTags,
        refetchOnMountOrArgChange: true,
    });
    const { removeQueryParam, setQueryParam } = useQueryParams();

    const patientSort = useAppSelector(selectPatientSort);
    const currentPatient = useAppSelector(selectCurrentPatient);
    const { setFilters, activeFilters, updateFilter, updateActiveFilterOptions, applyFilters } = useFilters();

    useEffect(() => {
        if (!selectedTrial) return;

        const filters = [
            ...(hasPermissions(["QC"])
                ? [
                      {
                          key: "qc",
                          label: "Status",
                          type: FilterType.OR,
                          filterOptions: [
                              { value: "qc", label: "QC" },
                              { value: "none", label: "None" },
                          ],
                          onFilter: (patient: IPatient, filterOption: FilterOption) => {
                              return patient.isQc === (filterOption.value === "qc");
                          },
                      },
                  ]
                : []),
            ...(canManageRemovedSubjects
                ? [
                      {
                          key: "removed",
                          label: "Removed",
                          type: FilterType.OR,
                          filterOptions: [{ value: "removed", label: "Show removed" }],
                          // Server-side filter: showRemoved=true returns active AND removed subjects
                          onFilter: () => true,
                      },
                  ]
                : []),
            ...(selectedTrial.showTags
                ? [
                      {
                          key: "tags",
                          label: "Trial Tags",
                          type: FilterType.AND,
                          filterOptions: null,
                          onFilter: (patient: IPatient, filterOption: FilterOption): boolean => {
                              return patient.tags.some((tag) => tag.uuid === filterOption.value);
                          },
                      },
                  ]
                : []),
            ...(selectedTrial.workflowActive
                ? [
                      {
                          key: "workflowState",
                          label: "Workflow States",
                          type: FilterType.OR,
                          filterOptions: null,
                          onFilter: (patient: IPatient, filterOption: FilterOption) => {
                              return patient?.workflowStates.some(
                                  (workflowState) => workflowState.value === filterOption.value
                              );
                          },
                      },
                  ]
                : []),
        ];

        const savedFilters = localStorage.getItem("activeFilters");

        if (savedFilters) {
            const parsedFilters = JSON.parse(savedFilters);
            const updatedFilters = filters.map((filter) => {
                const savedFilter = parsedFilters[filter.key];
                if (savedFilter) {
                    return {
                        ...filter,
                        activeFilterOptions: savedFilter,
                    };
                }
                return filter;
            });
            setFilters(updatedFilters);
        } else {
            setFilters(filters as FilterGroup[]);
        }
    }, [selectedTrial, canManageRemovedSubjects]);

    useEffect(() => {
        if (!workflowSates) return;

        const workflowStatesOptions = workflowSates?.map((state: WorkflowState) => ({
            value: state.value,
            label: state.label,
        }));

        updateFilter("workflowState", { filterOptions: workflowStatesOptions });
    }, [workflowSates, selectedTrial]);

    useEffect(() => {
        if (!tagOptions) return;

        const tagOptionsList = tagOptions.map((tag: any) => ({
            value: tag.uuid,
            label: tag.label,
        }));

        updateFilter("tags", { filterOptions: tagOptionsList });
    }, [tagOptions, selectedTrial]);

    const handleFilterChange = (filterKey: string, selectedOptionValues: FilterOption[]) => {
        updateActiveFilterOptions(filterKey, selectedOptionValues);
        const savedFilters = localStorage.getItem("activeFilters");
        if (filterKey === "qc" || filterKey === "removed") {
            setRequestTrigger("filter");
        }

        if (savedFilters) {
            const parsedFilters = JSON.parse(savedFilters);
            const updatedFilters = {
                ...parsedFilters,
                [filterKey]: selectedOptionValues,
            };

            return localStorage.setItem("activeFilters", JSON.stringify(updatedFilters));
        }

        return localStorage.setItem("activeFilters", JSON.stringify({ [filterKey]: selectedOptionValues }));
    };

    const [searchQuery, setSearchQuery] = useState<string>("");

    const supportedSortFields = useMemo(
        () =>
            new Set([
                "isQc",
                "patientId",
                "lastName",
                "site",
                "gender",
                "dateOfBirth",
                "visits",
                "latestISODate",
                "lastUploadedOnIso",
            ]),
        []
    );

    const selectedQcFilterOptions = activeFilters.get("qc")?.activeFilterOptions || [];
    const isQcFilter = useMemo(() => {
        if (selectedQcFilterOptions.length !== 1) return undefined;

        return selectedQcFilterOptions[0].value === "qc";
    }, [selectedQcFilterOptions]);

    const selectedRemovedFilterOptions = activeFilters.get("removed")?.activeFilterOptions || [];
    const showRemovedFilter = useMemo(() => {
        if (!canManageRemovedSubjects) return undefined;

        return selectedRemovedFilterOptions.length > 0 ? true : undefined;
    }, [selectedRemovedFilterOptions, canManageRemovedSubjects]);

    const sortBy = useMemo(() => {
        const sortField = patientSort.field as string;

        if (!supportedSortFields.has(sortField)) return undefined;

        return sortField;
    }, [patientSort.field, supportedSortFields]);

    useEffect(() => {
        setPage(0);
    }, [
        selectedTrial?.uuid,
        searchQuery,
        patientSort.field,
        patientSort.order,
        rowsPerPage,
        isQcFilter,
        showRemovedFilter,
    ]);

    const {
        data: patientsResponse,
        currentData: currentPatientsResponse,
        isFetching: isFetchingPatients,
        isLoading: patientsLoading,
        isUninitialized: patientsUninitialized,
    } = useGetPatientsQuery(
        {
            trialId: selectedTrial?.uuid || "",
            page: page + 1,
            limit: rowsPerPage,
            search: searchQuery || undefined,
            sortBy,
            sortOrder: patientSort.order === "asc" ? "asc" : "desc",
            isQc: isQcFilter,
            showRemoved: showRemovedFilter,
        },
        {
            refetchOnFocus: true,
            refetchOnReconnect: true,
            refetchOnMountOrArgChange: true,
            pollingInterval: 60_000,
            skip: !selectedTrial,
        }
    );
    const [, { isLoading: isChangingGroup }] = useUpdateCurrentGroupMutation({ fixedCacheKey: "groupChange" });

    useEffect(() => {
        setStablePatients([]);
        setStableTotal(0);
    }, [selectedTrial?.uuid]);

    useEffect(() => {
        const nextPatients = patientsResponse?.patients;

        if (Array.isArray(nextPatients) && !isFetchingPatients) {
            setStablePatients(nextPatients);
            setStableTotal(patientsResponse?.total || 0);
        }
    }, [patientsResponse, isFetchingPatients]);

    useEffect(() => {
        if (!isFetchingPatients) {
            setRequestTrigger(null);
        }
    }, [isFetchingPatients]);

    const filteredPatients = useMemo(() => {
        const patients = stablePatients;

        if (!patients.length) return [];

        return applyFilters(patients);
    }, [stablePatients, activeFilters]);

    const handlePatientClick = useCallback((event: React.MouseEvent<HTMLElement>, patient: IPatient) => {
        event.preventDefault();
        if (new URLSearchParams(window.location.search).get("selectedPatient") === patient.uuid) {
            removeQueryParam("selectedPatient", true);
        } else {
            setQueryParam("selectedPatient", patient.uuid, true);
        }
    }, []);

    const handleSort = useCallback(
        (event: React.MouseEvent<HTMLElement>, field: keyof IPatient) => {
            setRequestTrigger("sort");
            const isSameField = patientSort.field === field;
            const newOrder = isSameField ? (patientSort.order === "desc" ? "asc" : "desc") : "asc";
            dispatch(sortPatients({ field, order: newOrder }));
        },
        [patientSort.field, patientSort.order, dispatch]
    );

    const handleSearchQueryChange = useCallback((value: string) => {
        setRequestTrigger("search");
        setSearchQuery(value);
    }, []);

    const handlePageChange = useCallback((event: unknown, nextPage: number) => {
        setRequestTrigger("page");
        setPage(nextPage);
    }, []);

    const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setRequestTrigger("page");
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    const hasPatientsData = Boolean(
        stablePatients.length ||
            (currentPatientsResponse && currentPatientsResponse.patients) ||
            (patientsResponse && patientsResponse.patients)
    );

    const tableIsLoading = useMemo(() => {
        return (patientsLoading || patientsUninitialized || isChangingGroup) && !hasPatientsData;
    }, [patientsLoading, patientsUninitialized, isChangingGroup, hasPatientsData]);

    const isBackgroundRefreshing = useMemo(() => {
        return isFetchingPatients && hasPatientsData && !tableIsLoading && requestTrigger === null;
    }, [isFetchingPatients, hasPatientsData, tableIsLoading, requestTrigger]);

    const isSortLoading = useMemo(() => {
        return isFetchingPatients && requestTrigger === "sort";
    }, [isFetchingPatients, requestTrigger]);

    const shouldShowEmptyState = useMemo(() => {
        return !tableIsLoading && !isFetchingPatients && filteredPatients.length === 0;
    }, [tableIsLoading, isFetchingPatients, filteredPatients]);

    const hasClientSideFilters = useMemo(() => {
        return Array.from(activeFilters.values()).some(
            (filter) => filter.key !== "qc" && filter.key !== "removed" && Boolean(filter?.activeFilterOptions?.length)
        );
    }, [activeFilters]);

    const selectedPatientIsOutsideCurrentPage = useMemo(() => {
        if (!currentPatient?.uuid) return false;

        return !filteredPatients.some((patient) => patient.uuid === currentPatient.uuid);
    }, [currentPatient?.uuid, filteredPatients]);

    useEffect(() => {
        if (hasClientSideFilters) {
            dispatch(setFilteredPatientsCount(filteredPatients.length));
            return;
        }

        dispatch(setFilteredPatientsCount(stableTotal));
    }, [filteredPatients, dispatch, hasClientSideFilters, stableTotal]);

    return (
        <React.Fragment>
            <TableContainer sx={{ padding: "0 4px", height: "100%", display: "flex", flexDirection: "column" }}>
                <PatientSearch
                    activeFilters={activeFilters}
                    onFilterChange={handleFilterChange}
                    onQueryChange={handleSearchQueryChange}
                    searchQueryValue={searchQuery}
                />
                {selectedPatientIsOutsideCurrentPage ? (
                    <Box sx={{ px: 6, pb: 4 }}>
                        <DisplayText
                            text="Selected patient is not in this page after sorting/filtering."
                            type="bodySmall"
                        />
                    </Box>
                ) : null}
                <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                    <PatientTable
                        onSort={handleSort}
                        isLoading={tableIsLoading}
                        isSortLoading={isSortLoading}
                        activeSortField={patientSort.field}
                        onPatientClick={handlePatientClick}
                        patients={filteredPatients}
                        workflowSates={workflowSates}
                    />
                </Box>
                {shouldShowEmptyState ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <DisplayText text="No patients found" type="bodyLarge" />
                        {Array.from(activeFilters.values()).some((filter) => filter?.activeFilterOptions?.length) && (
                            <DisplayText text="(Check active filters applied)" type="bodySmall" />
                        )}
                    </Box>
                ) : null}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 6,
                        backgroundColor: "#29323f",
                    }}
                >
                    {isBackgroundRefreshing ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <DisplayText text="Updating patients..." type="bodySmall" />
                            <CircularProgress size={12} />
                        </Box>
                    ) : null}
                    <TablePagination
                        component="div"
                        count={stableTotal}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{
                            "width": "auto",
                            "flexShrink": 0,
                            "overflow": "hidden",
                            "& .MuiTablePagination-toolbar": {
                                minHeight: 40,
                                height: 40,
                                py: 0,
                            },
                            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                my: 0,
                            },
                        }}
                    />
                </Box>
            </TableContainer>
        </React.Fragment>
    );
};

export default PatientsDashboard;
